import { useEffect, useState } from 'react'
import { Button, Checkbox, Group, Modal, Select, Stack, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { extractApiError } from '../../lib/extractApiError'
import {
  useRTEmpresaCreate,
  useRTEmpresaReenviarAceite,
  useRTEmpresaUpdate,
} from '../../hooks/useRTsEmpresa'
import type { ConselhoRT, RTEmpresa } from '../../types'

interface Props {
  opened: boolean
  onClose: () => void
  empresaId: string
  empresaNome?: string
  emailSugerido?: string
  rt?: RTEmpresa | null
  onCreated?: (rt: RTEmpresa) => void
}

const CONSELHOS: Array<{ value: ConselhoRT; label: string }> = [
  { value: 'CREA', label: 'CREA' },
  { value: 'CFQ', label: 'CFQ' },
  { value: 'CRQ', label: 'CRQ' },
  { value: 'CRT', label: 'CRT' },
  { value: 'CRBio', label: 'CRBio' },
  { value: 'OUTRO', label: 'Outro' },
]

export default function RTEmpresaModal({
  opened,
  onClose,
  empresaId,
  empresaNome,
  emailSugerido,
  rt,
  onCreated,
}: Props) {
  const criar = useRTEmpresaCreate()
  const atualizar = useRTEmpresaUpdate(rt?.id ?? '')
  const enviarTermo = useRTEmpresaReenviarAceite()
  const [form, setForm] = useState({
    nome: '',
    titulo_profissional: '',
    conselho: 'CREA' as ConselhoRT,
    conselho_outro_descricao: '',
    numero_registro: '',
    uf: 'SP',
    rnp_confea: '',
    email: emailSugerido ?? '',
    is_principal: true,
  })

  useEffect(() => {
    if (!opened) return
    setForm(
      rt
        ? {
            nome: rt.nome,
            titulo_profissional: rt.titulo_profissional ?? '',
            conselho: rt.conselho,
            conselho_outro_descricao: rt.conselho_outro_descricao ?? '',
            numero_registro: rt.numero_registro,
            uf: rt.uf,
            rnp_confea: rt.rnp_confea ?? '',
            email: rt.email,
            is_principal: rt.is_principal,
          }
        : {
            nome: '',
            titulo_profissional: '',
            conselho: 'CREA',
            conselho_outro_descricao: '',
            numero_registro: '',
            uf: 'SP',
            rnp_confea: '',
            email: emailSugerido ?? '',
            is_principal: true,
          },
    )
  }, [emailSugerido, opened, rt])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    try {
      if (rt) {
        await atualizar.mutateAsync(form)
        notifications.show({
          color: 'green',
          message: 'Dados do responsável técnico atualizados.',
        })
        onClose()
        return
      }
      const novoRt = await criar.mutateAsync({ empresa: empresaId, ...form })
      try {
        await enviarTermo.mutateAsync(novoRt.id)
        notifications.show({
          color: 'green',
          message: `RT cadastrado e termo de aceite enviado para ${novoRt.email}.`,
        })
      } catch {
        notifications.show({
          color: 'orange',
          title: 'RT cadastrado',
          message: 'O cadastro foi salvo, mas o termo não pôde ser enviado. Reenvie na ficha da empresa.',
        })
      }
      onCreated?.(novoRt)
      onClose()
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível cadastrar o RT',
        message: extractApiError(err, 'Confira os dados profissionais.'),
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${rt ? 'Editar RT' : 'Novo RT'}${empresaNome ? ` — ${empresaNome}` : ''}`}
      centered
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <TextInput label="Nome completo" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.currentTarget.value })} />
          <TextInput label="Título profissional" placeholder="Ex.: Engenheiro Mecânico" value={form.titulo_profissional} onChange={(e) => setForm({ ...form, titulo_profissional: e.currentTarget.value })} />
          <Select label="Conselho" required data={CONSELHOS} value={form.conselho} onChange={(value) => value && setForm({ ...form, conselho: value as ConselhoRT })} />
          {form.conselho === 'OUTRO' && (
            <TextInput label="Nome do conselho" required value={form.conselho_outro_descricao} onChange={(e) => setForm({ ...form, conselho_outro_descricao: e.currentTarget.value })} />
          )}
          <Group grow>
            <TextInput label="Número do registro" required value={form.numero_registro} onChange={(e) => setForm({ ...form, numero_registro: e.currentTarget.value })} />
            <TextInput label="UF" required maxLength={2} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.currentTarget.value.toUpperCase() })} />
          </Group>
          <TextInput
            label="RNP Confea"
            placeholder="Opcional"
            value={form.rnp_confea}
            onChange={(e) => setForm({ ...form, rnp_confea: e.currentTarget.value })}
          />
          <TextInput label="E-mail" type="email" required description="Usado no aceite e nas notificações do RT." value={form.email} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
          <Checkbox label="RT principal desta empresa" checked={form.is_principal} onChange={(e) => setForm({ ...form, is_principal: e.currentTarget.checked })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancelar</Button>
            <Button
              type="submit"
              loading={criar.isPending || atualizar.isPending || enviarTermo.isPending}
            >
              {rt ? 'Salvar alterações' : 'Cadastrar RT'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
